import { useEffect, useMemo, useState } from "react";
import uaeGeo from "../data/uae.json";

const W = 700;
const H = 560;
const PAD = 46;

const PINS = [
    { name: "Abu Dhabi", lat: 24.4539, lon: 54.3773 },
    { name: "Sharjah", lat: 25.3463, lon: 55.4209 },
    { name: "Ajman", lat: 25.4052, lon: 55.5136 },
    { name: "Umm Al Quwain", lat: 25.5647, lon: 55.5552 },
    { name: "Ras Al Khaimah", lat: 25.7895, lon: 55.9432 },
    { name: "Fujairah", lat: 25.1288, lon: 56.3265 },
];
const HUB = { name: "Dubai", lat: 25.2048, lon: 55.2708 };

export const UaeMap = () => {
    const [dots, setDots] = useState([]);

    const project = useMemo(() => {
        const lons = [];
        const lats = [];
        const walk = (c) => {
            if (typeof c[0] === "number") {
                lons.push(c[0]);
                lats.push(c[1]);
            } else c.forEach(walk);
        };
        uaeGeo.features.forEach((f) => walk(f.geometry.coordinates));
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const meanLat = ((minLat + maxLat) / 2) * (Math.PI / 180);
        const sx = (W - PAD * 2) / ((maxLon - minLon) * Math.cos(meanLat));
        const sy = (H - PAD * 2) / (maxLat - minLat);
        const s = Math.min(sx, sy);
        return (lat, lon) => [
            PAD + (lon - minLon) * Math.cos(meanLat) * s,
            PAD + (maxLat - lat) * s,
        ];
    }, []);

    useEffect(() => {
        const cv = document.createElement("canvas");
        cv.width = W;
        cv.height = H;
        const ctx = cv.getContext("2d", { willReadFrequently: true });
        ctx.fillStyle = "#000";
        uaeGeo.features.forEach((f) => {
            const polys =
                f.geometry.type === "MultiPolygon"
                    ? f.geometry.coordinates
                    : [f.geometry.coordinates];
            polys.forEach((poly) =>
                poly.forEach((ring) => {
                    ctx.beginPath();
                    ring.forEach(([lon, lat], i) => {
                        const [x, y] = project(lat, lon);
                        if (i === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    });
                    ctx.closePath();
                    ctx.fill();
                }),
            );
        });
        const img = ctx.getImageData(0, 0, W, H).data;
        const pts = [];
        const step = 6;
        for (let y = 0; y < H; y += step)
            for (let x = 0; x < W; x += step)
                if (img[(y * W + x) * 4 + 3] > 100) pts.push([x, y]);
        setDots(pts);
    }, [project]);

    const [hx, hy] = project(HUB.lat, HUB.lon);

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            className="mx-auto h-auto w-full max-w-[720px]"
            data-testid="uae-map"
            role="img"
            aria-label="UAE map — NOMAS solution development across the seven emirates"
        >
            {dots.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={1.9} fill="#C3D4C6" />
            ))}

            {PINS.map((p) => {
                const [px, py] = project(p.lat, p.lon);
                return (
                    <g key={p.name}>
                        <line
                            x1={px}
                            y1={py}
                            x2={hx}
                            y2={hy}
                            stroke="#598B3B"
                            strokeWidth="1.1"
                            strokeDasharray="5 6"
                            opacity="0.6"
                            className="animate-dash"
                        />
                        <circle cx={px} cy={py} r="4.5" fill="#598B3B" />
                        <circle cx={px} cy={py} r="9" fill="#598B3B" opacity="0.16" />
                        <text
                            x={px}
                            y={py - 14}
                            textAnchor="middle"
                            fontFamily="Manrope, sans-serif"
                            fontSize="11"
                            fontWeight="600"
                            letterSpacing="1.5"
                            fill="#45543F"
                        >
                            {p.name.toUpperCase()}
                        </text>
                    </g>
                );
            })}

            <circle cx={hx} cy={hy} r="8" fill="#7CBF52" stroke="#598B3B" strokeWidth="2" />
            <circle
                cx={hx}
                cy={hy}
                r="8"
                fill="none"
                stroke="#7CBF52"
                strokeWidth="1.5"
                className="animate-pulse-ring"
            />
            <circle
                cx={hx}
                cy={hy}
                r="8"
                fill="none"
                stroke="#7CBF52"
                strokeWidth="1.5"
                className="animate-pulse-ring"
                style={{ animationDelay: "1.3s" }}
            />
            <text
                x={hx - 20}
                y={hy - 4}
                textAnchor="end"
                fontFamily="Outfit, sans-serif"
                fontSize="15"
                fontWeight="700"
                letterSpacing="3"
                fill="#0E1112"
            >
                SOLUTION DEVELOPMENT
            </text>
            <text
                x={hx - 20}
                y={hy + 16}
                textAnchor="end"
                fontFamily="Manrope, sans-serif"
                fontSize="11.5"
                fontWeight="500"
                letterSpacing="1.5"
                fill="#5C6B57"
            >
                Across the UAE — Dubai Hub
            </text>
        </svg>
    );
};
